import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Sparkles, ShieldCheck, CalendarClock, AlertTriangle, Loader2, Info } from "lucide-react";

const PLAN_FEATURES = {
  parent: ["Browse & RSVP for events and meet-ups", "Message local vendors", "Access the full resource hub", "Join meet-up chats"],
  vendor: ["Listed in the vendor directory", "Upload logo, photos & PDF flyers", "Receive parent enquiries", "Post community events"],
  event_creator: ["Publish community events", "Event dashboard with status"],
  marketplace_user: ["List physical products in the marketplace", "Reach vetted local families", "Manage listings from your dashboard", "Buyer contact routing"],
};

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [agreeLaunchTerms, setAgreeLaunchTerms] = useState(false);
  const result = params.get("result");
  const selectedRef = useRef(null);

  const load = () => {
    api.get("/billing/plans").then((r) => {
      setPlans(r.data);
      // Prefer the first Launch plan as the default selection — the Standard
      // Rate card is temporarily disabled, so it cannot be the initial choice.
      const first = r.data.find((p) => p.plan_id.includes("launch")) || r.data[0];
      if (first && !selectedRef.current) {
        selectedRef.current = first.plan_id;
        setSelected(first.plan_id);
      }
    }).catch(() => {});
    api.get("/billing/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  if (user.role === "admin") {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="subscribe-admin">
        <ShieldCheck className="w-10 h-10 text-sage mx-auto mb-3" />
        <h1 className="text-2xl font-black text-forest mb-2">Admin accounts don't need a subscription</h1>
        <Link to="/admin"><Button className="rounded-full bg-sage hover:bg-sage-dark text-white mt-4" data-testid="goto-admin-btn">Go to admin console</Button></Link>
      </div>
    );
  }

  const isActive = status?.subscription_status === "active";
  const sub = status?.subscription;

  const subscribe = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/initialize", { plan_id: selected });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error("Could not start checkout");
        setBusy(false);
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You can resubscribe anytime.");
      setConfirmCancel(false);
      await refresh();
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 grain" data-testid="subscribe-page">
        <OnboardingProgress />
      <div className="text-center mb-8 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Compass Kids membership</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">
          {isActive ? "Your subscription" : `Choose your ${user.role.replace("_", " ")} plan`}
        </h1>
        <p className="text-forest/60 mt-2 max-w-xl mx-auto">
          {isActive
            ? "Manage your membership below."
            : "One simple monthly subscription unlocks the whole community. Cancel anytime."}
        </p>
      </div>

      {result === "cancelled" && (
        <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-sm text-amber-800" data-testid="payment-cancelled-banner">
          <AlertTriangle className="w-4 h-4" />
          Checkout was cancelled — no charge was made. Pick a plan whenever you're ready.
        </div>
      )}
      {status && !status.gateway_ready && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-sm text-amber-800 text-center" data-testid="gateway-setup-banner">
          Payment gateway is in setup mode. Live billing activates once the Paystack keys are added.
        </div>
      )}

      {isActive && sub ? (
        <div className="max-w-lg mx-auto bg-white/80 border border-sage/20 rounded-3xl p-8 shadow-sm" data-testid="active-subscription-card">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-11 h-11 rounded-xl bg-sage-light flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-sage-dark" />
            </span>
            <div>
              <h2 className="font-heading font-bold text-forest text-lg">{sub.plan_id ? sub.plan_id.replace(/_/g, " ") : "Membership"}</h2>
              <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full">Active</span>
            </div>
          </div>
          <div className="space-y-2 text-sm text-forest/70 mb-6">
            <p className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-sage-dark" /> R{(sub.amount_cents / 100).toFixed(0)}/month, billed automatically via Paystack</p>
            {sub.current_period_end && (
              <p className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-sage-dark" /> Next billing date: {new Date(sub.current_period_end).toLocaleDateString()}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={dashboardFor({ ...user, subscription_status: "active" })}>
              <Button className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="continue-to-app-btn">Continue to the app</Button>
            </Link>
            {!confirmCancel ? (
              <Button variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => setConfirmCancel(true)} data-testid="cancel-subscription-btn">
                Cancel subscription
              </Button>
            ) : (
              <div className="flex items-center gap-2" data-testid="cancel-confirm">
                <span className="text-xs text-forest/60">Sure? Access ends at period end.</span>
                <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark" onClick={cancel} disabled={busy} data-testid="cancel-confirm-btn">
                  Yes, cancel
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setConfirmCancel(false)} data-testid="cancel-abort-btn">Keep it</Button>
              </div>
            )}
          </div>
        </div>
      ) : (() => {
        const launchPlan = plans.find((p) => p.plan_id.includes("launch")) || plans[0];
        const normalPlan = plans.find((p) => p.plan_id.includes("normal"));
        return (
        <>
          <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto mb-8" data-testid="plan-grid">
            {plans.map((p) => {
              const isLaunch = p.plan_id.includes("launch");
              // Standard Rate card is temporarily disabled — visible but
              // non-selectable until we manually re-enable it.
              const isDisabled = !isLaunch;
              const selectedCls = isDisabled
                ? "border-sage/20 bg-softstone/50 opacity-60 cursor-not-allowed"
                : (selected === p.plan_id ? "border-sage ring-2 ring-sage/40 bg-white" : "border-sage/20 bg-white/70 hover:border-sage/50");
              const features = PLAN_FEATURES[user.role] || [];
              const launchTermsText = (user.role === "event_creator" || user.role === "marketplace_user" || user.role === "vendor")
                ? "This R149/month launch offer is only available for a strictly limited time. Sign up now to lock in this discounted rate for your first 12 months. Thereafter, your subscription will automatically resume at the standard price of R299/month. By selecting this option, you agree to these terms and conditions."
                : "This R99/month launch offer is only available for a strictly limited time. Sign up now to lock in this discounted rate for your first 12 months. Thereafter, your subscription will automatically resume at the standard price of R149/month. By selecting this option, you agree to these terms and conditions.";
              const showLaunchTerms = isLaunch && (user.role === "parent" || user.role === "event_creator" || user.role === "marketplace_user" || user.role === "vendor");
              const onCardClick = () => { if (isDisabled) return; selectedRef.current = p.plan_id; setSelected(p.plan_id); };
              return (
                <div
                  key={p.plan_id}
                  role={isDisabled ? undefined : "button"}
                  tabIndex={isDisabled ? -1 : 0}
                  onClick={onCardClick}
                  onKeyDown={(e) => { if (!isDisabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onCardClick(); } }}
                  aria-disabled={isDisabled}
                  className={`text-left rounded-3xl border-2 p-6 transition-all shadow-sm ${selectedCls}`}
                  data-testid={`plan-card-${p.plan_id}`}
                >
                  {isLaunch && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full mb-3">
                      <Sparkles className="w-3 h-3" /> Launch special
                    </span>
                  )}
                  {isDisabled && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-forest/10 text-forest/60 px-2.5 py-1 rounded-full mb-3" data-testid={`plan-unavailable-${p.plan_id}`}>
                      Currently unavailable
                    </span>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black font-heading text-forest">R{p.amount_cents / 100}</span>
                    <span className="text-sm text-forest/50">/month</span>
                  </div>
                  <p className="text-sm font-bold text-forest mt-1">{p.tier}{isLaunch && (<> <span className="text-xs font-semibold text-terracotta-dark">Limited Time</span></>)}</p>
                  <ul className="mt-4 space-y-2">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-forest/70">
                        <CheckCircle2 className="w-3.5 h-3.5 text-sage-dark shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  {user.role === "event_creator" && (
                    <p className="mt-3 text-xs font-semibold text-forest bg-sage-light/60 border border-sage/30 rounded-xl px-3 py-2" data-testid="event-creator-3-event-notice">
                      Your subscription includes up to 3 active events at a time.
                    </p>
                  )}
                  {showLaunchTerms ? (
                    <div className="mt-4 rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/40 p-3 text-left" data-testid="launch-terms-block" onClick={(e) => e.stopPropagation()}>
                      <p className="text-[11px] text-forest/80 leading-relaxed mb-2" data-testid="launch-terms-text">
                        {launchTermsText}
                      </p>
                      <label className="flex items-start gap-2 text-xs font-bold text-forest cursor-pointer select-none" data-testid="launch-terms-label">
                        <input
                          type="checkbox"
                          checked={agreeLaunchTerms}
                          onChange={(e) => setAgreeLaunchTerms(e.target.checked)}
                          className="mt-0.5 w-5 h-5 shrink-0 accent-sage-dark cursor-pointer border-2 border-forest/40 rounded"
                          data-testid="launch-terms-checkbox"
                        />
                        <span>I have read and agree to the terms above.</span>
                      </label>
                    </div>
                  ) : (
                    <div className={`mt-4 text-center text-xs font-bold py-1.5 rounded-full ${isDisabled ? "bg-forest/10 text-forest/50" : (selected === p.plan_id ? "bg-sage text-white" : "bg-softstone text-forest/50")}`}>
                      {isDisabled ? "Unavailable" : (selected === p.plan_id ? "Selected" : "Tap to select")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-center">
            <p
              className="text-[11px] text-forest/60 mb-3 flex items-start sm:items-center justify-center gap-1.5 max-w-md mx-auto leading-snug"
              data-testid="r1-verification-notice"
            >
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
              <span>Paystack charges a refundable R1 card verification fee to activate your payment method. It's refunded in full and is not part of your subscription cost.</span>
            </p>
            <Button
              size="lg"
              onClick={subscribe}
              disabled={busy || !selected || !status?.gateway_ready || ((user.role === "parent" || user.role === "event_creator" || user.role === "marketplace_user" || user.role === "vendor") && !agreeLaunchTerms)}
              className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-lg px-10 hover:-translate-y-0.5 transition-transform"
              data-testid="subscribe-now-btn"
            >
              {busy ? "Redirecting to Paystack..." : "Subscribe Now"}
            </Button>
            <p className="text-xs text-forest/50 mt-3 flex items-center justify-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure payment via Paystack · Billed monthly · Cancel anytime
            </p>
          </div>
        </>
        );
      })()}
    </div>
  );
}
